import { Gender, PositionGroup } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { atomWithStorage, unwrap } from 'jotai/utils';
import { clamp, isEqual } from 'lodash';
import { playerPositionsOptionsAtom } from '@/atoms/queries/player/playerPositions';
import {
  playerMetricDistributionPercentilesAtom,
  playerMetricPercentilesAndBucketsAtom,
} from '@/atoms/queries/players';
import { getPercentileLimits } from '@/components/modal/CreateRadarTemplateModal/metricLimitsUtils';
import {
  MIN_SELECTED_METRICS,
  POSITION_TEMPLATE_MAP_MEN,
  POSITION_TEMPLATE_MAP_WOMEN,
  TEAM_RADAR_TEMPLATE_CONFIG_IDS_MEN,
  TEAM_RADAR_TEMPLATE_CONFIG_IDS_WOMEN,
} from '@/consts/radar';
import { CONFIG_NAME_MAX_LENGTH } from '@/consts/userConfigs';
import { MinMax } from '@/types/metric';
import { LimitMode, RadarData, RadarDataFormat } from '@/types/radar';
import { RadarTemplateModalTab } from '@/types/tabs';
import { UserConfigActions, UserConfigRadarTemplate, UserConfigType } from '@/types/userConfigs';
import { getOnDemandIncludeParams } from '@/utils/metrics';
import { isValidEnumValue } from '@/utils/predicates';
import { defaultTemplateIdForPosition, resolvePositionGroup } from '@/utils/radar';
import { metricsAtom } from './metric';
import { teamMetricDistributionPercentilesAtom, teamMetricPercentilesAndBucketsAtom } from './queries/teams';
import {
  genderAtom,
  playerRadarTemplateConfigsAtom,
  radarTemplatesAtom,
  settingsConfigAtom,
  teamRadarTemplateConfigsAtom,
  unwrappedPlayerRadarTemplatesAtom,
  unwrappedTeamRadarTemplatesAtom,
} from './queries/userConfigs';
import { radarDataFormatParamAtom } from './queryParams';
import { sectionAtom } from './section';
import { userConfigActionAtom, userConfigIdAtom, userConfigNameAtom, userConfigTypeAtom } from './userConfigs';

export const renderedRadarDataAtom = atom<RadarData[]>([]);
export const renderedComparisonRadarDataAtom = atom<RadarData[]>([]);
export const unwrappedRenderedRadarDataAtom = unwrap(renderedRadarDataAtom, prev => prev || null);

/* This will probably need to change when the templates come from the DB */
export const selectedRadarTemplateIdAtom = atom<string | null>(null);

// The template to fall back to when nothing has been explicitly selected — derived from the
// section, gender and the player's most-played position (Center Forward when there's no position
// data). Shared by the radar dropdown and the summary profile dropdown so both open on the same
// default while keeping their own independent selections.
export const defaultTemplateIdAtom = atom(async (get): Promise<string> => {
  const gender = await get(genderAtom);
  const section = get(sectionAtom);

  // on team radar page, default to attacking template based on selected gender
  if (section === 'team' && gender === Gender.M) return TEAM_RADAR_TEMPLATE_CONFIG_IDS_MEN.ATTACKING;
  if (section === 'team' && gender === Gender.F) return TEAM_RADAR_TEMPLATE_CONFIG_IDS_WOMEN.ATTACKING;

  // on player radar page, default based on most played position and gender
  const positionMap = gender === Gender.M ? POSITION_TEMPLATE_MAP_MEN : POSITION_TEMPLATE_MAP_WOMEN;
  const positions = await get(playerPositionsOptionsAtom);

  // an unknown or missing position code would otherwise index to undefined and silently empty the default
  // on both the radar and summary dropdowns, so it falls back to the centre forward template
  return defaultTemplateIdForPosition(positions[0]?.value, positionMap) ?? positionMap.cf;
});

export const radarTemplateIdAtom = atom(
  async get => {
    const selectedRadarTemplateId = get(selectedRadarTemplateIdAtom);
    const section = get(sectionAtom);

    // Honour an explicit selection only while it is still available for the current player/team.
    // A persisted selection can become invalid when the context changes - e.g. Physical is filtered
    // out for goalkeepers - so fall back to the default rather than resolving to a missing template.
    if (selectedRadarTemplateId && (section === 'player' || section === 'team')) {
      const availableConfigs = await get(
        section === 'team' ? teamRadarTemplateConfigsAtom : playerRadarTemplateConfigsAtom,
      );
      if (availableConfigs.some(config => config.configId === selectedRadarTemplateId)) {
        return selectedRadarTemplateId;
      }
      return get(defaultTemplateIdAtom);
    }

    if (selectedRadarTemplateId) return selectedRadarTemplateId;
    return get(defaultTemplateIdAtom);
  },
  (_get, set, id: string) => {
    set(selectedRadarTemplateIdAtom, id);
  },
);

export const unwrappedRadarTemplateIdAtom = unwrap(radarTemplateIdAtom, prev => prev || null);

// The profile selected in the AI summary drawer. Independent of the radar dropdown: it shares the
// same default but has its own selection state, so changing one does not affect the other.
export const selectedSummaryProfileIdAtom = atom<string | null>(null);

export const summaryProfileIdAtom = atom(
  async get => {
    const selectedSummaryProfileId = get(selectedSummaryProfileIdAtom);
    if (selectedSummaryProfileId) return selectedSummaryProfileId;
    return get(defaultTemplateIdAtom);
  },
  (_get, set, id: string) => {
    set(selectedSummaryProfileIdAtom, id);
  },
);

// Resolves the summary profile id to its full config. Reads playerRadarTemplateConfigsAtom — the
// same list the summary dropdown offers — so any selectable id (and the default) should always
// resolve. Falls back to null (not undefined) so consumers can distinguish "still resolving" from
// "resolved but no matching template found".
export const selectedSummaryProfileAtom = atom(async get => {
  const [templates, selectedId] = await Promise.all([get(playerRadarTemplateConfigsAtom), get(summaryProfileIdAtom)]);
  return templates.find(template => template.configId === selectedId) ?? null;
});

export const unwrappedSelectedSummaryProfileAtom = unwrap(selectedSummaryProfileAtom, () => undefined);

export const selectedRadarTemplateTitleSettingsAtom = atom(get => {
  const section = get(sectionAtom);
  const unwrappedRadarTemplateId = get(unwrappedRadarTemplateIdAtom);

  if (section === 'dataLocker') return null;

  const templatesAtom = section === 'player' ? unwrappedPlayerRadarTemplatesAtom : unwrappedTeamRadarTemplatesAtom;
  const templates = get(templatesAtom);
  return templates.find(template => template.id === unwrappedRadarTemplateId)?.titleSettings;
});

const radarTemplateModalActiveStepBaseAtom = atom<0 | 1 | 2>(0);
export const radarTemplateModalActiveStepAtom = atom(
  get => get(radarTemplateModalActiveStepBaseAtom),
  (_get, set, step: number) => {
    const clampedStep = clamp(step, 0, 2) as 0 | 1 | 2;
    set(radarTemplateModalActiveStepBaseAtom, clampedStep);
  },
);
export const radarTemplatePositionGroupAtom = atom<PositionGroup | null>(null);
export const radarTemplateNameAtom = atom('');
export const radarTemplateActiveTabAtom = atom(RadarTemplateModalTab.AVAILABLE_METRICS);
export const radarTemplateActiveMetricAtom = atom('');

export const isRadarTemplateModalOpenAtom = atom(get => {
  const userConfigAction = get(userConfigActionAtom);
  const userConfigId = get(userConfigIdAtom);
  const userConfigType = get(userConfigTypeAtom);

  const isCreateMode = userConfigAction === UserConfigActions.CREATE;
  const isDuplicateMode = userConfigAction === UserConfigActions.DUPLICATE;
  const isEditMode = userConfigAction === UserConfigActions.EDIT && !!userConfigId;
  const isRadarTemplateConfig = userConfigType === UserConfigType.RADAR_TEMPLATE;

  return isRadarTemplateConfig && (isCreateMode || isDuplicateMode || isEditMode);
});

export const openRadarTemplateModalForCreateAtom = atom(null, (_get, set) => {
  set(userConfigActionAtom, UserConfigActions.CREATE);
  set(userConfigTypeAtom, UserConfigType.RADAR_TEMPLATE);
});

export const radarTemplateCurrentMetricAtom = atom(
  get => {
    const radarTemplateActiveMetric = get(radarTemplateActiveMetricAtom);
    const selectedMetrics = get(metricsAtom);

    // if there is an active metric, use that
    if (radarTemplateActiveMetric) return radarTemplateActiveMetric;

    // otherwise return the first metric in the list as the default
    return selectedMetrics[0];
  },
  (_, set, metric: string) => {
    set(radarTemplateActiveMetricAtom, metric);
  },
);

/** The limit mode for each metric ('default' or 'custom') in an object of  metricKey -> limitMode */
export const radarTemplateLimitModesAtom = atom<Record<string, LimitMode>>({});

/** Custom min/max limits for each metric in an object of metricKey -> minMaxObject */
export const radarTemplateCustomLimitsAtom = atom<Record<string, MinMax>>({});

export const closeAndResetRadarTemplateModalAtom = atom(null, (_get, set) => {
  set(radarTemplateModalActiveStepAtom, 0);
  set(radarTemplatePositionGroupAtom, null);
  set(radarTemplateNameAtom, '');
  set(metricsAtom, []);
  set(radarTemplateActiveTabAtom, RadarTemplateModalTab.AVAILABLE_METRICS);
  set(radarTemplateActiveMetricAtom, '');
  set(radarTemplateLimitModesAtom, {});
  set(radarTemplateCustomLimitsAtom, {});
  set(userConfigActionAtom, null);
  set(userConfigIdAtom, null);
  set(userConfigTypeAtom, null);
});

export const selectedRadarTemplateAtom = atom(async get => {
  const radarTemplates = await get(radarTemplatesAtom);
  const selectedTemplateId = await get(radarTemplateIdAtom);
  return radarTemplates.find(template => template.configId === selectedTemplateId);
});

export const unwrappedSelectedRadarTemplateAtom = unwrap(selectedRadarTemplateAtom, prev => prev);

export const selectedRadarTemplateIncludeParamsAtom = atom(get =>
  getOnDemandIncludeParams({
    metricKeys: get(unwrappedSelectedRadarTemplateAtom)?.definition.metrics.map(({ key }) => key),
  }),
);

// The radar's selected template's group, resolved with the same lookup as the AI summary
// (resolvePositionGroup), with the player's most played position, as defaultTemplateIdAtom uses.
export const selectedPlayerTemplatePositionGroupAtom = atom(async get => {
  const [selectedTemplate, radarTemplates] = await Promise.all([
    get(selectedRadarTemplateAtom),
    get(radarTemplatesAtom),
  ]);
  // The position is only needed for a template without a group, so a template with one doesn't wait on it.
  const mostPlayedPosition = selectedTemplate?.definition.positionGroup
    ? undefined
    : (await get(playerPositionsOptionsAtom))[0]?.value;
  return resolvePositionGroup(selectedTemplate, mostPlayedPosition, radarTemplates).positionGroup;
});

export const unwrappedSelectedPlayerTemplatePositionGroupAtom = unwrap(
  selectedPlayerTemplatePositionGroupAtom,
  prev => prev ?? null,
);

const radarTemplateNameErrorAtom = atom(async get => {
  // trim whitespace for validation
  const name = get(radarTemplateNameAtom).trim();
  const isTooLong = name.length > CONFIG_NAME_MAX_LENGTH;
  if (isTooLong) return 'maxLength';

  const section = get(sectionAtom);
  const userConfigAction = get(userConfigActionAtom);
  const selectedRadarTemplate = await get(selectedRadarTemplateAtom);
  const radarTemplateConfigs = await get(radarTemplatesAtom);
  // Check if name is already taken for that entity (player/team)
  const isAlreadyTaken = radarTemplateConfigs.some(
    ({ definition }) => definition.titleSettings.title === name && definition.entity === section,
  );
  const isEditMode = userConfigAction === UserConfigActions.EDIT;

  // if we're in edit mode, we want to allow the user to keep the same name if they want to
  if (isAlreadyTaken && (!isEditMode || selectedRadarTemplate?.definition.titleSettings.title !== name)) {
    return 'alreadyExists';
  }

  return undefined;
});
export const unwrappedRadarTemplateNameErrorAtom = unwrap(radarTemplateNameErrorAtom, prev => prev || undefined);

// determines whether the radar template name is valid
const isValidRadarTemplateNameAtom = atom(get => {
  const radarTemplateNameError = get(unwrappedRadarTemplateNameErrorAtom);
  const name = get(radarTemplateNameAtom);

  return !!name && !radarTemplateNameError;
});

export const radarTemplateCustomDistributionsAtom = atom<{ key: string; min: number; max: number }[]>(get => {
  const radarTemplateLimitModes = get(radarTemplateLimitModesAtom);
  const radarTemplateCustomLimits = get(radarTemplateCustomLimitsAtom);

  const customDistributions = Object.entries(radarTemplateLimitModes)
    .filter(([, limitMode]) => limitMode === 'custom')
    .map(([metricKey]) => ({
      key: metricKey,
      min: radarTemplateCustomLimits[metricKey].min,
      max: radarTemplateCustomLimits[metricKey].max,
    }));

  return customDistributions;
});

const radarTemplateDistributionsAtom = atom(async get => {
  const customDistributions = get(radarTemplateCustomDistributionsAtom);
  const metrics = get(metricsAtom);
  const isPlayerSection = get(sectionAtom) === 'player';

  const metricPercentilesAndBucketsAtom = isPlayerSection
    ? playerMetricPercentilesAndBucketsAtom
    : teamMetricPercentilesAndBucketsAtom;
  const { percentiles } = await get(metricPercentilesAndBucketsAtom);

  // Map() from an array is last-key-wins; .find() is first-match. customDistributions' keys are
  // unique by construction (built from Object.entries), but percentiles' uniqueness depends on the
  // section's data source, so build first-wins to preserve .find()'s exact semantics either way.
  const customDistributionsByKey = new Map(customDistributions.map(dist => [dist.key, dist]));
  const percentilesByMetricKey = percentiles.reduce((map, percentile) => {
    if (!map.has(percentile.metricKey)) map.set(percentile.metricKey, percentile);
    return map;
  }, new Map<string, (typeof percentiles)[number]>());

  return metrics.map(metric => {
    const customDistribution = customDistributionsByKey.get(metric);
    const isCustomDistribution = !!customDistribution;

    const metricDistribution = percentilesByMetricKey.get(metric);
    if (!metricDistribution) throw new Error(`No metric distribution found for metric: ${metric}`);

    const { min: fifthPercentile, max: ninetyFifthPercentile } = getPercentileLimits(metricDistribution);
    const min = isCustomDistribution ? customDistribution.min : fifthPercentile;
    const max = isCustomDistribution ? customDistribution.max : ninetyFifthPercentile;

    return {
      key: metricDistribution.metricKey,
      keyNormalised: metricDistribution.metricKeyNormalised,
      min,
      max,
      default: !isCustomDistribution,
    };
  });
});

export const unwrappedRadarTemplateDistributionsAtom = unwrap(radarTemplateDistributionsAtom, prev => prev || []);

/**
 * Return whether the 'done' button on the final page of the custom radar wizard is disabled or not. This is necessary
 * because a user might click 'back' from this page to change the selected metrics, then, when they 'continue', we need
 * to disable the 'done' button until the new set of distributions are loaded.
 */
export const isDisabledDoneButtonAtom = atom(get => {
  const distributions = get(unwrappedRadarTemplateDistributionsAtom);
  const selectedMetrics = get(metricsAtom);

  const distributionMetricKeys = new Set(distributions.map(distribution => distribution.key));
  const selectedMetricKeys = new Set(selectedMetrics);

  return !isEqual(distributionMetricKeys, selectedMetricKeys);
});

// Returns whether the Continue button (on step 1 or 2 of the wizard) should be disabled
export const isDisabledContinueButtonAtom = atom(get => {
  const radarTemplateModalActiveStep = get(radarTemplateModalActiveStepAtom);
  const isValidRadarTemplateName = get(isValidRadarTemplateNameAtom);
  const selectedMetrics = get(metricsAtom);
  const isMissingPlayerPositionGroup = get(sectionAtom) === 'player' && !get(radarTemplatePositionGroupAtom);

  const isDisabledStep1 = !isValidRadarTemplateName || isMissingPlayerPositionGroup;
  const isDisabledStep2 = radarTemplateModalActiveStep === 1 && selectedMetrics.length < MIN_SELECTED_METRICS;

  return isDisabledStep1 || isDisabledStep2;
});

const getMetricPercentilesAtomForSection = (section: string) =>
  section === 'player' ? playerMetricDistributionPercentilesAtom : teamMetricDistributionPercentilesAtom;

const createMetricPercentilesAtom = (radarDataAtom: typeof renderedRadarDataAtom) =>
  atom(async get => {
    const section = get(sectionAtom);
    const renderedRadarData = get(radarDataAtom);

    if (!renderedRadarData.length) return {};

    const percentiles = await get(getMetricPercentilesAtomForSection(section ?? ''));

    if (!percentiles.length) return {};

    const percentilesMap = new Map(percentiles.map(percentileData => [percentileData.metricKey, percentileData]));

    return renderedRadarData.reduce<Record<string, number>>((acc, { key: metricKey, value: metricValue, hasValue }) => {
      // no underlying data — leave the key out so the table shows n/a rather than a fake percentile
      if (!hasValue) return acc;

      const percentileData = percentilesMap.get(metricKey);
      if (!percentileData) return acc;

      const matchingPercentile = percentileData.percentiles.find(
        percentileEntry =>
          (percentileData.percentage ? percentileEntry.value * 100 : percentileEntry.value) >= metricValue,
      );

      // If no matching percentile found, value exceeds p100, so use 100
      const rawPercentile = matchingPercentile ? matchingPercentile.percentile : 100;
      acc[metricKey] = percentileData.reverseScale ? 100 - rawPercentile : rawPercentile;
      return acc;
    }, {});
  });

export const radarTableMetricPercentilesAtom = createMetricPercentilesAtom(renderedRadarDataAtom);
export const radarTableComparisonMetricPercentilesAtom = createMetricPercentilesAtom(renderedComparisonRadarDataAtom);

export const unwrappedRadarTableMetricPercentilesAtom = unwrap(radarTableMetricPercentilesAtom, prev => prev ?? {});
export const unwrappedRadarTableComparisonPercentilesAtom = unwrap(
  radarTableComparisonMetricPercentilesAtom,
  prev => prev ?? {},
);

export const radarDataFormatAtom = atom(async get => {
  const radarDataFormatParam = get(radarDataFormatParamAtom);
  if (radarDataFormatParam && isValidEnumValue(RadarDataFormat, radarDataFormatParam)) return radarDataFormatParam;

  const settingsConfig = await get(settingsConfigAtom);
  const radarDataFormatFromSettings = settingsConfig?.definition?.radar?.dataFormat;
  if (radarDataFormatFromSettings) return radarDataFormatFromSettings;

  return RadarDataFormat.Per90;
});

export const unwrappedRadarDataFormatAtom = unwrap(radarDataFormatAtom, prev => prev || RadarDataFormat.Per90);

export type RadarView = 'radar' | 'distribution';
export const RADAR_VIEW = { RADAR: 'radar', DISTRIBUTION: 'distribution' } as const satisfies Record<string, RadarView>;
export const radarViewAtom = atomWithStorage<RadarView>('radarView', RADAR_VIEW.RADAR, undefined, { getOnInit: true });

export const setRadarTemplateEditAtom = atom(
  null,
  (
    _get,
    set,
    {
      template,
      isDuplicate = false,
      name,
    }: { template: UserConfigRadarTemplate; isDuplicate?: boolean; name?: string },
  ) => {
    const { definition } = template;
    const metrics = definition.metrics.map(metric => metric.key);
    const { limitModes, customLimits } = definition.metrics.reduce(
      (acc, metric) => {
        if (!metric.default) {
          acc.limitModes[metric.key] = 'custom';
          acc.customLimits[metric.key] = { min: metric.min, max: metric.max };
        }
        return acc;
      },
      { limitModes: {} as Record<string, LimitMode>, customLimits: {} as Record<string, MinMax> },
    );

    set(userConfigIdAtom, isDuplicate ? null : template.configId);
    set(userConfigTypeAtom, UserConfigType.RADAR_TEMPLATE);
    set(userConfigActionAtom, isDuplicate ? UserConfigActions.DUPLICATE : UserConfigActions.EDIT);
    set(radarTemplateModalActiveStepAtom, 0);
    set(radarTemplatePositionGroupAtom, definition.positionGroup);
    set(radarTemplateNameAtom, name ?? definition.titleSettings.title);
    set(metricsAtom, metrics);
    set(radarTemplateActiveTabAtom, RadarTemplateModalTab.AVAILABLE_METRICS);
    set(radarTemplateLimitModesAtom, limitModes);
    set(radarTemplateCustomLimitsAtom, customLimits);
  },
);

export const setRadarTemplateDeleteAtom = atom(null, (_get, set, configId: string) => {
  set(userConfigIdAtom, configId);
  set(userConfigActionAtom, UserConfigActions.DELETE);
  set(userConfigTypeAtom, UserConfigType.RADAR_TEMPLATE);
});

export const setRadarTemplateRenameAtom = atom(null, (_get, set, radarTemplate: UserConfigRadarTemplate) => {
  set(userConfigIdAtom, radarTemplate.configId);
  set(userConfigNameAtom, radarTemplate.definition.titleSettings.title);
  set(userConfigActionAtom, UserConfigActions.RENAME);
  set(userConfigTypeAtom, UserConfigType.RADAR_TEMPLATE);
});
